import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { useCallback, useContext, useEffect, useState } from 'react'
import AppContext from '../../../context/appContext'
import { useMonitoringSites } from '../../../hooks/data/useMonitoringSites'
import { useSamplingAreas } from '../../../hooks/data/useSamplingAreas'
import { useCanEditExclusionAreas } from '../../../hooks/useCanEditExclusionAreas'
import { useUserRole } from '../../../hooks/useUserRole'
import { a11yProps } from '../../../utils/tabsHandle'
import StratificationHome from '../../Classification/Stratification/StratificationHome'
import EventPlanning from '../../EventPlanning/EventPlanning'
import TabPanel from '../../NavBar/TabPanel'
import SamplePlanningHome from '../../SamplePlanning/SamplePlanningHome'
import SOCSample from '../../SOCSample/SOCSample'
import { ExclusionAreaProvider } from '../ExclusionArea/ExclusionAreaContext'
import { ExclusionAreaHome } from '../ExclusionArea/ExclusionAreaHome'
import { StratificationHome as QUStratificationHome } from '../Stratification/StratificationHome'

const MonitoringHome = () => {
  const [value, setValue] = useState(0)
  const [mapWidth] = useState((window.innerWidth / 3) * 2)
  const [mapHeight] = useState(window.innerHeight)
  const {
    partialChanges,
    setPartialChanges,
    setConfirmationModalConfig,
    innerTab,
    hideNavbar,
    currentFarm,
    programConfig,
  } = useContext(AppContext)
  const { isViewOnly, isAdmin, isGISTechnician } = useUserRole()
  const canEditExclusionAreas = useCanEditExclusionAreas()
  const socSampleTabEnable = programConfig?.formDefinitions?.monitoringSOCSample?.enable || false
  const quConfigEnabled = !!programConfig?.quConfig?.enabled

  const { data: monitoringSites = [] } = useMonitoringSites({ farmId: currentFarm?.id })
  const { data: samplingAreas, isLoading: samplingAreasLoading } = useSamplingAreas({
    farmId: currentFarm?.id,
  })
  const samplingAreasSaved = (samplingAreas?.length ?? 0) > 0

  const handleCanEditClassification = useCallback(
    mS => {
      if (isAdmin || isGISTechnician) return true

      if (isViewOnly) return false

      // If there are random monitoring sites, classifications are disabled
      // (unless user is admin/GIS technician, which we already checked above)
      return !mS.some(monitorSite => monitorSite.isRandomSite)
    },
    [isAdmin, isGISTechnician, isViewOnly],
  )

  const canEditClassification = handleCanEditClassification(monitoringSites)

  // The Plan panel is only mounted while the gate is open, so a gate that closes under the
  // operator would leave the tab selected but disabled with no panel matching it — an empty
  // content area. Fall back to the stratification tab that owns the strata.
  // Waiting for the query to settle keeps a deep link on Plan instead of bouncing it off the
  // still-empty in-flight state; an error settles too, and must bounce like a genuine empty.
  useEffect(() => {
    if (!samplingAreasLoading && !samplingAreasSaved && value === 2) setValue(1)
  }, [samplingAreasLoading, samplingAreasSaved, value])

  const handleTabChange = (event, newValue) => {
    if (partialChanges) {
      setConfirmationModalConfig({
        open: true,
        title: 'Cambios sin guardar',
        message: 'Si cambia de pantalla, se perderán los cambios. ¿Desea continuar?',
        confirmLabel: 'Continuar',
        cancelLabel: 'Cancelar',
        confirmAction: () => {
          localStorage.setItem('monitoring-home-last-index', newValue)
          setPartialChanges(false)
          setValue(newValue)
        },
      })
    } else {
      localStorage.setItem('monitoring-home-last-index', newValue)
      setValue(newValue)
    }
  }

  useEffect(() => {
    const lastIndex = localStorage.getItem('monitoring-home-last-index')
    if (!lastIndex) {
      localStorage.setItem('monitoring-home-last-index', 0)
      setValue(0)
    }

    // Tab interna en caso de redirección
    if (innerTab) {
      setValue(innerTab)
    }
  }, [innerTab])

  return (
    <>
      {currentFarm && socSampleTabEnable !== null && (
        <Box sx={{ width: '100%' }}>
          <Box
            display={hideNavbar ? 'none' : 'inherit'}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tabs
              aria-label="monitoring tabs"
              gap={2}
              value={value || 0}
              onChange={handleTabChange}
            >
              <Tab label="Área de exclusión" sx={{ textTransform: 'none' }} {...a11yProps(0)} />
              <Tab label="Estratificación" sx={{ textTransform: 'none' }} {...a11yProps(1)} />
              <Tab
                label="Plan"
                sx={{ textTransform: 'none' }}
                {...a11yProps(2)}
                disabled={!samplingAreasSaved}
              />
              <Tab label="Eventos" sx={{ textTransform: 'none' }} {...a11yProps(3)} />
              {socSampleTabEnable && (
                <Tab label="Suelos" sx={{ textTransform: 'none' }} {...a11yProps(4)} />
              )}
            </Tabs>
          </Box>
          <TabPanel index={0} value={value}>
            <ExclusionAreaProvider canEdit={canEditExclusionAreas}>
              <ExclusionAreaHome />
            </ExclusionAreaProvider>
          </TabPanel>
          <TabPanel index={1} value={value}>
            {quConfigEnabled ? (
              <QUStratificationHome />
            ) : (
              <StratificationHome
                canEditClassification={canEditClassification}
                mapHeight={mapHeight}
                mapWidth={mapWidth}
                setTabValue={handleTabChange}
              />
            )}
          </TabPanel>
          {samplingAreasSaved && (
            <TabPanel index={2} value={value}>
              <SamplePlanningHome />
            </TabPanel>
          )}
          <TabPanel index={3} value={value}>
            <EventPlanning />
          </TabPanel>
          <TabPanel index={4} value={value}>
            <SOCSample />
          </TabPanel>
        </Box>
      )}
    </>
  )
}

export default MonitoringHome
